import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Form, message, Row } from 'antd';
import { useCreateOfficeMutation } from 'gql/types';
import { Button, FormItem, Spin } from 'modules/common/components';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFormFields } from './fields';
import { IAddOfficeFormSchema, addOfficeFormSchema } from './schema';

const defaultValues = {
  name: undefined,
  zoetisAccountNumber: undefined,
  idexxAccountNumber: undefined,
  regionId: undefined,
};

const CreateOfficeForm: FC = () => {
  const { t } = useTranslation('user.CreateLocationsForm');
  const { control, formState, handleSubmit, reset } =
    useForm<IAddOfficeFormSchema>({
      resolver: yupResolver(addOfficeFormSchema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues,
    });
  const [createOffice, createOfficeMutation] = useCreateOfficeMutation({
    refetchQueries: ['getOffices'],
    onCompleted: () => {
      message.success(t('officeCreated'));
      reset();
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const formFields = useMemo(() => {
    return getFormFields({ control });
  }, []);

  const submitForm = handleSubmit((formData) => {
    createOffice({
      variables: {
        input: formData,
      },
    });
  });

  return (
    <Spin spinning={createOfficeMutation.loading}>
      <Form>
        <Row gutter={[10, 0]} align="top">
          {formFields.map((field) => {
            const { Component, getErrorMessage, name } = field;
            const errorMessage = getErrorMessage(formState.errors);
            const validateStatus = errorMessage && 'error';
            return (
              <Col span={5} key={name}>
                <FormItem extra={errorMessage} validateStatus={validateStatus}>
                  <Component />
                </FormItem>
              </Col>
            );
          })}
          <Col span={4}>
            <FormItem>
              <Button
                htmlType="submit"
                onClick={submitForm}
                type="primary"
                block
                disabled={!formState.isValid}
              >
                {t('create')}
              </Button>
            </FormItem>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default CreateOfficeForm;
